import { Directive, ElementRef, HostBinding, HostListener, Renderer2, signal, inject, output } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

@Directive({
	selector: '[upload]',
	standalone: true,
})
export class UploadDirective {
	private renderer = inject(Renderer2);
	private el = inject(ElementRef);
	private transloco = inject(TranslocoService);

	@HostBinding('class.fileover') fileOver: boolean = false;
	readonly fileDropped = output<any>();

	private overlayElement: HTMLElement | null = null;
	private dragCounter = signal(0);

	@HostListener('dragenter', ['$event']) onDragEnter(evt: DragEvent) {
		evt.preventDefault();
		evt.stopPropagation();
		this.dragCounter.set(this.dragCounter() + 1);

		if (this.dragCounter() === 1) {
			// Only create overlay on the first dragenter
			this.fileOver = true;
			this.createOverlay();
		}
	}

	@HostListener('dragleave', ['$event']) public onDragLeave(evt: DragEvent) {
		evt.preventDefault();
		evt.stopPropagation();

		this.dragCounter.set(this.dragCounter() - 1);
		if (this.dragCounter() === 0) {
			// Only remove the overlay if no more drag items are present
			this.fileOver = false;
			if (this.overlayElement) {
				this.removeOverlay();
			}
		}
	}

	@HostListener('drop', ['$event']) public onDrop(evt: DragEvent) {
		this.drop(evt);
	}

	private drop(evt: DragEvent) {
		evt.preventDefault();
		evt.stopPropagation();

		// Reset the counter and clean up the overlay
		this.dragCounter.set(0); // Reset to ensure new drag events work correctly
		this.fileOver = false;
		if (this.overlayElement) {
			this.removeOverlay();
		}

		// Handle the dropped files
		const files = evt.dataTransfer?.files;
		if (files && files.length > 0) {
			this.fileDropped.emit(files); // Emit the files to the parent component
		}
	}

	private createOverlay() {
		this.overlayElement = this.renderer.createElement('div');
		this.renderer.setStyle(this.overlayElement, 'position', 'absolute');
		this.renderer.setStyle(this.overlayElement, 'top', '0');
		this.renderer.setStyle(this.overlayElement, 'left', '0');
		this.renderer.setStyle(this.overlayElement, 'width', '100%');
		this.renderer.setStyle(this.overlayElement, 'height', '100%');
		this.renderer.setStyle(this.overlayElement, 'backgroundColor', 'rgba(0, 0, 0, 0.6)');
		this.renderer.setStyle(this.overlayElement, 'backdropFilter', 'blur(8px)');
		this.renderer.setStyle(this.overlayElement, '-webkit-backdrop-filter', 'blur(8px)');
		this.renderer.setStyle(this.overlayElement, 'display', 'flex');
		this.renderer.setStyle(this.overlayElement, 'flexDirection', 'column');
		this.renderer.setStyle(this.overlayElement, 'alignItems', 'center');
		this.renderer.setStyle(this.overlayElement, 'justifyContent', 'center');
		this.renderer.setStyle(this.overlayElement, 'zIndex', '10');
		this.renderer.setStyle(this.overlayElement, 'borderRadius', '8px');
		this.renderer.setStyle(this.overlayElement, 'opacity', '0'); // Start hidden
		this.renderer.setStyle(this.overlayElement, 'transition', 'opacity 0.5s ease-in');

		// Add event listeners to the overlay itself
		this.renderer.listen(this.overlayElement, 'dragover', (event: DragEvent) => {
			event.preventDefault();
			event.stopPropagation();
		});

		this.renderer.listen(this.overlayElement, 'drop', (event: DragEvent) => {
			this.drop(event);
		});

		// Add the "+" icon
		const overlayIcon = this.renderer.createElement('span');
		this.renderer.setStyle(overlayIcon, 'fontSize', '3rem');
		this.renderer.setStyle(overlayIcon, 'color', 'white');
		const iconTextNode = this.renderer.createText('+');
		this.renderer.appendChild(overlayIcon, iconTextNode);
		this.renderer.appendChild(this.overlayElement, overlayIcon);

		// Add custom text
		const overlayText = this.renderer.createElement('span');
		this.renderer.setStyle(overlayText, 'fontSize', '1.5rem');
		this.renderer.setStyle(overlayText, 'color', 'white');
		this.renderer.setStyle(overlayText, 'marginTop', '0.5rem');
		const textNode = this.renderer.createText(this.transloco.translate('common.dropFile'));
		this.renderer.appendChild(overlayText, textNode);
		this.renderer.appendChild(this.overlayElement, overlayText);

		// Append the overlay to the dropzone
		this.renderer.appendChild(this.el.nativeElement, this.overlayElement);

		// Trigger the fade-in animation
		setTimeout(() => {
			this.renderer.setStyle(this.overlayElement, 'opacity', '1');
		}, 0);
	}

	private removeOverlay() {
		if (this.overlayElement) {
			this.renderer.setStyle(this.overlayElement, 'opacity', '0'); // Trigger fade-out
			setTimeout(() => {
				this.renderer.removeChild(this.el.nativeElement, this.overlayElement);
				this.overlayElement = null; // Ensure it's null so a new one can be created
			}, 500); // Matches the fade-out duration
		}
	}
}
